/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clipboardCopy from "clipboard-copy";
import * as url from "helper/UrlHelper";
import downloadFileHandler from "helper/DownloadFile";

// ── helpers (local to modal) ─────────────────────────────────────────────────

const getExtension = (name = "") => (name.split(".").pop() || "").toLowerCase();

const FILE_TYPES = {
  image: ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"],
  video: ["mp4", "avi", "mov", "webm", "mkv", "flv"],
  audio: ["m4a", "mp3", "wav", "ogg", "aac", "flac"],
  pdf: ["pdf"],
  word: ["doc", "docx"],
  excel: ["xls", "xlsx", "csv"],
  ppt: ["ppt", "pptx"],
  text: ["txt", "rtf"],
  code: ["py", "js", "ts", "xml", "json", "html", "css", "dll"],
};

const getFileType = (name = "") => {
  const ext = getExtension(name);
  for (const [type, exts] of Object.entries(FILE_TYPES)) {
    if (exts.includes(ext)) return type;
  }
  return "other";
};

const TYPE_META = {
  image: { icon: "image", color: "#10B981", bg: "#D1FAE5", label: "Image" },
  video: {
    icon: "play_circle",
    color: "#8B5CF6",
    bg: "#EDE9FE",
    label: "Video",
  },
  audio: {
    icon: "graphic_eq",
    color: "#F59E0B",
    bg: "#FEF3C7",
    label: "Audio",
  },
  pdf: {
    icon: "picture_as_pdf",
    color: "#EF4444",
    bg: "#FEE2E2",
    label: "PDF",
  },
  word: { icon: "description", color: "#2563EB", bg: "#DBEAFE", label: "Word" },
  excel: {
    icon: "table_chart",
    color: "#059669",
    bg: "#D1FAE5",
    label: "Excel",
  },
  ppt: { icon: "co_present", color: "#EA580C", bg: "#FFEDD5", label: "PPT" },
  text: { icon: "article", color: "#64748B", bg: "#F1F5F9", label: "Text" },
  code: { icon: "code", color: "#7C3AED", bg: "#EDE9FE", label: "Code" },
  other: {
    icon: "insert_drive_file",
    color: "#6B7280",
    bg: "#F3F4F6",
    label: "File",
  },
};

const buildFileUrl = (path = "") => url.SERVER_URL.replace(/\/$/, "") + path;

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ── Image preview with error fallback ────────────────────────────────────────

const ImagePreview = ({ fileUrl, name, meta, t }) => {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 88, height: 88, background: meta.bg }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 48, color: meta.color }}
          >
            broken_image
          </span>
        </div>
        <p
          className="mb-1 fw-semibold text-dark"
          style={{ fontSize: "0.9rem" }}
        >
          {t("Image could not be loaded")}
        </p>
        <p
          className="mb-0 text-muted small text-center"
          style={{ maxWidth: 340 }}
        >
          {t("The file may not be accessible from this browser.")}
        </p>
        <div className="d-flex gap-2 flex-wrap justify-content-center mt-1">
          <a
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 15 }}
            >
              open_in_new
            </span>
            {t("Open in new tab")}
          </a>
          <button
            type="button"
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => downloadFileHandler({ path: fileUrl, name })}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 15 }}
            >
              download
            </span>
            {t("Download")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex align-items-center justify-content-center p-3"
      style={{ minHeight: 200, background: "#f8fafc", width: "100%" }}
    >
      <img
        src={fileUrl}
        alt={name}
        style={{
          maxWidth: "100%",
          maxHeight: 500,
          borderRadius: 8,
          objectFit: "contain",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
        }}
        onError={() => setErrored(true)}
      />
    </div>
  );
};

// ── Text / Code preview (fetches raw content) ──────────────────────────────

const TextPreview = ({ fileUrl, ext }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setContent(null);
    fetch(fileUrl)
      .then((r) => r.text())
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [fileUrl]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 gap-2">
        <div className="spinner-border spinner-border-sm text-primary" />
        <span className="text-muted small">Loading content…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5 text-danger">
        <span className="material-symbols-outlined" style={{ fontSize: 40 }}>
          error_outline
        </span>
        <p className="mt-2 mb-0 small">Could not load file content.</p>
      </div>
    );
  }

  return (
    <pre
      style={{
        maxHeight: 460,
        overflow: "auto",
        fontSize: "0.79rem",
        lineHeight: 1.6,
        background: "#0f172a",
        color: "#e2e8f0",
        padding: "16px 20px",
        borderRadius: 0,
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontFamily: "'Fira Code', 'Cascadia Code', Consolas, monospace",
      }}
    >
      {content}
    </pre>
  );
};

// ── PDF preview via <object> — shows fallback when URL is inaccessible ──────

const PdfPreview = ({ fileUrl, name, meta, t }) => (
  <div style={{ background: "#f8fafc" }}>
    <object
      data={fileUrl}
      type="application/pdf"
      style={{ width: "100%", height: 560, display: "block", border: "none" }}
    >
      {/* Fallback — shown when PDF can't be loaded / rendered by the browser */}
      <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 88, height: 88, background: meta.bg }}
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 48, color: meta.color }}
          >
            picture_as_pdf
          </span>
        </div>
        <p
          className="mb-1 fw-semibold text-dark"
          style={{ fontSize: "0.9rem" }}
        >
          {t("PDF cannot be previewed in the browser")}
        </p>
        <p
          className="mb-0 text-muted small text-center"
          style={{ maxWidth: 340 }}
        >
          {t("The file may not be accessible or requires a PDF viewer plugin.")}
        </p>
        <div className="d-flex gap-2 flex-wrap justify-content-center mt-1">
          <a
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 15 }}
            >
              open_in_new
            </span>
            {t("Open in new tab")}
          </a>
          <button
            type="button"
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => downloadFileHandler({ path: fileUrl, name })}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 15 }}
            >
              download
            </span>
            {t("Download PDF")}
          </button>
        </div>
      </div>
    </object>
  </div>
);

// ── Office / Google-Docs-Viewer iframe with loading + fallback notice ────────

const OfficePreview = ({ fileUrl, name, meta, t }) => {
  const [loaded, setLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // After 12 s without the iframe firing onLoad, assume the viewer couldn't load
  useEffect(() => {
    setLoaded(false);
    setShowFallback(false);
    const timer = setTimeout(() => setShowFallback(true), 12000);
    return () => clearTimeout(timer);
  }, [fileUrl]);

  return (
    <div style={{ position: "relative", background: "#f8fafc" }}>
      {/* Spinner overlay while loading */}
      {!loaded && !showFallback && (
        <div
          className="d-flex flex-column align-items-center justify-content-center gap-2"
          style={{ minHeight: 200, paddingTop: 32, paddingBottom: 32 }}
        >
          <div
            className="spinner-border text-primary"
            style={{ width: 28, height: 28 }}
          />
          <span className="text-muted small">
            {t("Loading document preview…")}
          </span>
        </div>
      )}

      {/* Fallback when Google Docs Viewer can't access the file */}
      {showFallback && (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: 88, height: 88, background: meta.bg }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 48, color: meta.color }}
            >
              {meta.icon}
            </span>
          </div>
          <p
            className="mb-1 fw-semibold text-dark"
            style={{ fontSize: "0.9rem" }}
          >
            {t("Document preview unavailable")}
          </p>
          <p
            className="mb-0 text-muted small text-center"
            style={{ maxWidth: 340 }}
          >
            {t("The file may not be publicly accessible for online preview.")}
          </p>
          <div className="d-flex gap-2 flex-wrap justify-content-center mt-1">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 15 }}
              >
                open_in_new
              </span>
              {t("Open in new tab")}
            </a>
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center gap-1"
              onClick={() => downloadFileHandler({ path: fileUrl, name })}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 15 }}
              >
                download
              </span>
              {t("Download file")}
            </button>
          </div>
        </div>
      )}

      {/* Iframe — rendered while waiting; hidden until loaded */}
      {!showFallback && (
        <iframe
          src={`https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`}
          title={name}
          style={{
            width: "100%",
            height: 540,
            border: "none",
            display: loaded ? "block" : "none",
          }}
          allowFullScreen
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
};

// ── No-preview fallback ─────────────────────────────────────────────────────

const NoPreview = ({ meta, fileUrl, name, t }) => (
  <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
    <div
      className="d-flex align-items-center justify-content-center rounded-circle"
      style={{ width: 88, height: 88, background: meta.bg }}
    >
      <span
        className="material-symbols-outlined"
        style={{ fontSize: 48, color: meta.color }}
      >
        {meta.icon}
      </span>
    </div>
    <p className="mb-0 text-muted fw-semibold">
      {t("No preview available for this file type")}
    </p>
    <button
      type="button"
      className="btn btn-primary btn-sm d-flex align-items-center gap-1"
      onClick={() => downloadFileHandler({ path: fileUrl, name })}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
        download
      </span>
      {t("Download to view")}
    </button>
  </div>
);

// ── Copy button with transient feedback ─────────────────────────────────────

const CopyButton = ({
  label,
  icon,
  value,
  copiedKey,
  id,
  onCopy,
  variant = "outline-secondary",
}) => {
  const copied = copiedKey === id;
  return (
    <button
      className={`btn btn-sm btn-${variant} d-flex align-items-center gap-1`}
      style={{ fontSize: "0.75rem" }}
      onClick={() => onCopy(id, value)}
      title={label}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
        {copied ? "check_circle" : icon}
      </span>
      {copied ? "Copied!" : label}
    </button>
  );
};

// ── Main modal ───────────────────────────────────────────────────────────────

const FilePreviewModal = ({ selectedItem, setSelectedItem = () => {} }) => {
  const { t } = useTranslation();
  const [copiedKey, setCopiedKey] = useState(null);

  const handleCopy = (key, text) => {
    clipboardCopy(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2200);
  };

  // Reset copied indicator whenever file changes
  useEffect(() => {
    setCopiedKey(null);
  }, [selectedItem]);

  const item = selectedItem || {};
  const fileUrl = item.path ? buildFileUrl(item.path) : "";
  const fileType = getFileType(item.name);
  const meta = TYPE_META[fileType] || TYPE_META.other;
  const ext = getExtension(item.name || "");

  const renderPreview = () => {
    if (!item.path) return null;

    switch (fileType) {
      case "image":
        return (
          <ImagePreview fileUrl={fileUrl} name={item.name} meta={meta} t={t} />
        );

      case "video":
        return (
          <div style={{ background: "#000", borderRadius: 0 }}>
            <video
              controls
              style={{ width: "100%", maxHeight: 500, display: "block" }}
            >
              <source src={fileUrl} />
              {t("Your browser does not support the video tag.")}
            </video>
          </div>
        );

      case "audio":
        return (
          <div
            className="d-flex flex-column align-items-center justify-content-center gap-3 py-4"
            style={{ background: "#0f172a", minHeight: 200 }}
          >
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 96,
                height: 96,
                background: "rgba(245,158,11,0.15)",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 56, color: "#F59E0B" }}
              >
                graphic_eq
              </span>
            </div>
            <p
              className="mb-0 fw-semibold text-white text-center"
              style={{ fontSize: "0.85rem", maxWidth: 320 }}
            >
              {item.name}
            </p>
            <div style={{ width: "90%", maxWidth: 480 }}>
              <audio controls style={{ width: "100%" }}>
                <source src={fileUrl} />
              </audio>
            </div>
          </div>
        );

      case "pdf":
        return (
          <PdfPreview fileUrl={fileUrl} name={item.name} meta={meta} t={t} />
        );

      case "text":
      case "code":
        return <TextPreview fileUrl={fileUrl} ext={ext} />;

      case "word":
      case "excel":
      case "ppt":
        return (
          <OfficePreview fileUrl={fileUrl} name={item.name} meta={meta} t={t} />
        );

      default:
        return (
          <NoPreview meta={meta} fileUrl={fileUrl} name={item.name} t={t} />
        );
    }
  };

  return (
    <div
      className="modal fade"
      id="filePreviewModal"
      tabIndex="-1"
      aria-labelledby="filePreviewModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content bg-white border-0 rounded-15 overflow-hidden">
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div className="modal-header bg-primary text-white p-4 pe-4 pb-3 border-0">
            <div className="d-flex align-items-center gap-3 overflow-hidden">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: 44,
                  height: 44,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                <span
                  className="material-symbols-outlined text-white"
                  style={{ fontSize: 22 }}
                >
                  preview
                </span>
              </div>
              <div className="overflow-hidden">
                <h3
                  className="fw-bold text-white mb-0"
                  id="filePreviewModalLabel"
                  style={{ fontSize: "1rem" }}
                >
                  {t("File Preview")}
                </h3>
                <p
                  className="text-white mb-0 text-truncate"
                  style={{ fontSize: "0.78rem", opacity: 0.85, maxWidth: 480 }}
                  title={item.name}
                >
                  {item.name || t("No file selected")}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => setSelectedItem(null)}
            />
          </div>

          {/* ── Info / action bar ───────────────────────────────────────────── */}
          {item.path && (
            <div
              className="d-flex flex-wrap align-items-center gap-2 px-4 py-2 border-bottom"
              style={{ background: "#f8fafc" }}
            >
              {/* File type badge */}
              <span
                className="badge d-flex align-items-center gap-1"
                style={{
                  background: meta.bg,
                  color: meta.color,
                  border: `1px solid ${meta.color}44`,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  padding: "4px 8px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 13 }}
                >
                  {meta.icon}
                </span>
                {meta.label} · {ext.toUpperCase()}
              </span>

              {/* Date */}
              {item.createdAt && (
                <span
                  className="badge bg-light text-muted d-flex align-items-center gap-1"
                  style={{
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    padding: "4px 8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 13 }}
                  >
                    calendar_today
                  </span>
                  {formatDate(item.createdAt)}
                </span>
              )}

              {/* Action buttons pushed to the right */}
              <div className="ms-auto d-flex flex-wrap gap-2">
                <CopyButton
                  id="url"
                  label={t("Copy URL")}
                  icon="link"
                  value={fileUrl}
                  copiedKey={copiedKey}
                  onCopy={handleCopy}
                  variant="outline-primary"
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                  style={{ fontSize: "0.75rem" }}
                  onClick={() =>
                    downloadFileHandler({ path: fileUrl, name: item.name })
                  }
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 14 }}
                  >
                    download
                  </span>
                  {t("Download")}
                </button>
              </div>
            </div>
          )}

          {/* ── Preview area ────────────────────────────────────────────────── */}
          <div className="modal-body p-0" style={{ minHeight: 200 }}>
            {item.path ? (
              <div className="overflow-hidden" style={{ borderRadius: 0 }}>
                {renderPreview()}
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted gap-2">
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 52, opacity: 0.3 }}
                >
                  perm_media
                </span>
                <p className="mb-0 small">{t("No file selected")}</p>
              </div>
            )}
          </div>

          {/* ── Footer ─────────────────────────────────────────────────────── */}
          {item.path && (
            <div
              className="px-4 py-2 border-top d-flex align-items-center gap-2"
              style={{
                background: "#f8fafc",
                fontSize: "0.72rem",
                color: "#94a3b8",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 13 }}
              >
                folder_open
              </span>
              <span
                className="text-truncate"
                style={{
                  fontFamily: "monospace",
                  maxWidth: "calc(100% - 24px)",
                }}
                title={item.path}
              >
                {item.name}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
